import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { ProjectPermissionRoles } from 'mapistry-shared';
import { Api } from '@monorepo/shared/apiClient';
import { useCallback } from 'react';
import { useMutation } from 'react-query';

type UseResendUserInviteParams = {
  projectId: string;
  userId: string;
  userName: string;
  role: ProjectPermissionRoles | string;
};

const SSO_SUCCESS_MESSAGE =
  "An invitation email will not be sent. The user belongs to an organization that manages their login credentials via SSO. If they are setup in the organization's Identity Provider they will be able to login and access this site.";

const EMAIL_SUCCESS_MESSAGE =
  'The invitation was created. An email will be sent within 10 minutes.';

export function useResendUserInvite({
  projectId,
  userId,
  userName,
  role,
}: UseResendUserInviteParams) {
  const { success, showUserFriendlyErrorToast } = useToast();
  const [resendProjectInvitation] = useMutation(Api.resendProjectInvitation, {
    throwOnError: true,
  });

  return useCallback(async () => {
    try {
      const result = await resendProjectInvitation({
        projectId,
        userId,
        role,
      });
      if (!result) {
        throw new Error('Resend invite response was empty.');
      }
      success(result.isSsoUser ? SSO_SUCCESS_MESSAGE : EMAIL_SUCCESS_MESSAGE);
    } catch (err) {
      showUserFriendlyErrorToast(
        err,
        `Unable to resend the invite to ${userName}.`,
      );
    }
  }, [
    projectId,
    resendProjectInvitation,
    role,
    showUserFriendlyErrorToast,
    success,
    userId,
    userName,
  ]);
}
